import { useForm } from 'react-hook-form';
import React, { useEffect, useState, useMemo } from 'react';
import { Row, Button, Container } from 'react-bootstrap';

import '../BookingConfirmation/index.scss';
import Loading from '../Loading';
import { ConfirmationHeader, CustomModal } from '../Reusable';
import { applyPortalTextColor, dispatchModal, formatDateTimeWithDayAbbr, formatToFixed } from '../../utils/helper';
import { bookingConfirmation, getBookingConfirmationDetailsService } from '../../services/bookingsService';
import {
    CustomerInfoSection,
    FinancialsAndContactSection,
    ItineraryTabsSection,
    PaymentsDueSection,
    PoweredByFooter,
} from '../BookingConfirmation';

const ConfirmationActionSection = ({ bookingDetails, settings, loading, onConfirmClick }) => {
    return (
        <div>
            {bookingDetails.detailsConfirmedByCustomerName && bookingDetails.detailsConfirmedByCustomerAt ? (
                <div className="mt-3" style={{ color: settings.textColor, fontSize: '20px' }}>
                    Details confirmed by <b>{bookingDetails.detailsConfirmedByCustomerName}</b> on{' '}
                    <b>{formatDateTimeWithDayAbbr(bookingDetails.detailsConfirmedByCustomerAt)}</b>
                </div>
            ) : (
                <>
                    <Button
                        className="mt-3"
                        style={{
                            backgroundColor: loading ? '#646262' : settings.buttonColor,
                            borderColor: 'transparent',
                        }}
                        onClick={onConfirmClick}
                    >
                        CONFIRM DETAILS
                    </Button>
                    <p className="mt-3" style={{ color: settings.textColor }}>
                        *If your details do not appear correct in their entirety, please contact your Salesperson
                        directly to make the necessary changes
                    </p>
                </>
            )}
        </div>
    );
};

const BookingReview = (props) => {
    const queryObj = useMemo(() => Object.fromEntries(new URLSearchParams(window.location.search)), []);
    const { bid: bookingId, ts } = queryObj;

    const { register, handleSubmit } = useForm();
    const [state, setState] = useState({
        loading: true,
        confirm: false,
        contractId: null,
        formData: { name: '' },
        data: {
            bookingDetails: {},
            contactDetails: {},
            routeData: [],
            tripSetting: {},
            totals: {},
        },
    });

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const bookingRes = await getBookingConfirmationDetailsService(bookingId, ts);

                if (!isMounted) return;

                const booking = bookingRes;
                if (booking && Object.keys(booking.data).length > 0) {
                    const { details, contactDetail, routeData, accountSettings } = booking.data;

                    const root = document.documentElement;
                    root?.style.setProperty('--background-color', accountSettings.mainColor);
                    root?.style.setProperty('--background-color-active', accountSettings.textColor);
                    applyPortalTextColor(accountSettings.portalTextColor);

                    setState((prev) => ({
                        ...prev,
                        loading: false,
                        data: {
                            bookingDetails: details,
                            contactDetails: contactDetail,
                            routeData: routeData,
                            tripSetting: accountSettings,
                            totals: {
                                bookingTotal: details.totalAfterTax,
                                vehiclesTotal: formatToFixed(details.vehicleTotal),
                                addonsTotal: formatToFixed(details.addonTotal),
                                totalDiscount: formatToFixed(details.totalDiscount),
                            },
                        },
                    }));
                } else {
                    dispatchModal('Booking Record Not Found.');
                    setState((prev) => ({ ...prev, loading: false }));
                }
            } catch (error) {
                console.error(error);
                setState((prev) => ({ ...prev, loading: false }));
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId, ts]);

    const handleConfirmSubmit = async (e) => {
        e.preventDefault();
        await handleSubmit(async (formData) => {
            try {
                setState((prev) => ({ ...prev, loading: true, formData: { name: formData.name } }));
                await bookingConfirmation(bookingId, formData.name);
                window.location.reload();
            } catch (e) {
                console.error(e);
                setState((prev) => ({ ...prev, loading: false, confirm: false, formData: { name: '' } }));
            }
        })();
    };

    const { loading, confirm, data } = state;
    const { bookingDetails, contactDetails, routeData, tripSetting, totals } = data;

    if (loading) return <Loading loading={loading} />;

    return (
        <div>
            <div className="top-header">
                <ConfirmationHeader tripSetting={tripSetting} bookingDisplayId={bookingDetails.bookingId} />

                <div className="background-box-color">
                    <div className="background-wrapper" style={{ backgroundColor: tripSetting.sectionColor }}>
                        <Container fluid>
                            <Row>
                                {/* Customer Info */}
                                <CustomerInfoSection contactDetails={contactDetails} settings={tripSetting} />

                                {/* Financials & Sales */}
                                <FinancialsAndContactSection
                                    bookingDetails={bookingDetails}
                                    totals={totals}
                                    settings={tripSetting}
                                />
                            </Row>

                            {/* Itinerary Tabs */}
                            <ItineraryTabsSection
                                routeData={routeData}
                                bookingDetails={bookingDetails}
                                settings={tripSetting}
                            />

                            {/* Payments Due */}
                            <PaymentsDueSection bookingDetails={bookingDetails} settings={tripSetting} />

                            {/* Confirmation Action Button */}
                            <ConfirmationActionSection
                                bookingDetails={bookingDetails}
                                settings={tripSetting}
                                loading={loading}
                                onConfirmClick={() => setState((p) => ({ ...p, confirm: true }))}
                            />

                            {confirm && (
                                <CustomModal
                                    show={confirm}
                                    onHide={() => setState((p) => ({ ...p, confirm: false }))}
                                    settings={tripSetting}
                                    onSubmit={handleConfirmSubmit}
                                    loading={loading}
                                    register={register}
                                    type="Confirm"
                                />
                            )}
                        </Container>
                    </div>
                </div>
            </div>
            <PoweredByFooter />
        </div>
    );
};

export default React.memo(BookingReview, () => true);
